<script setup>
import { computed } from 'vue'

import AuditId from './AuditId.vue'
import { clip, duration, useAuditText } from './audit-text.js'

const props = defineProps({
  // 平台审计里的一次调用（详情响应 calls 的一项）；请求与响应正文查不到时为 null，finish_reason、protocol 平台没有记录时为 null。
  call: { type: Object, required: true },
})

// 超过这个长度的消息默认折叠，只露出开头一行。
const LONG_MESSAGE = 600

const { t, tr } = useAuditText()

/** 消息正文按文本拼：字符串原样；分段内容取各段文字，非文字段（图片等）写成"[类型]"。 */
function messageText(message) {
  const content = message?.content
  if (typeof content === 'string') return content
  if (Array.isArray(content)) return content.map((part) => (typeof part === 'string' ? part : part?.text ?? `[${part?.type ?? '?'}]`)).join('\n')
  return ''
}

const messages = computed(() => {
  const list = props.call.request_body?.messages
  if (!Array.isArray(list)) return []
  return list.map((message, index) => {
    const text = messageText(message)
    return { key: index, role: String(message?.role ?? '—'), text, long: text.length > LONG_MESSAGE, preview: clip(text.replace(/\s+/g, ' '), 96) }
  })
})
</script>

<template>
  <div class="au-callbody" data-testid="observer-call-detail">
    <p class="au-hint">
      {{ tr('calls.callId') }} <AuditId v-if="call.llm_call_id" :id="call.llm_call_id" /><span v-else>—</span> · {{ tr('calls.authNotRecorded') }}
    </p>
    <dl class="au-kv">
      <dt>{{ t('visualGameAdmin.storyCalls.task') }}</dt>
      <dd>{{ call.task || '—' }}</dd>
      <dt>{{ t('visualGameAdmin.storyCalls.mode') }}</dt>
      <dd>{{ call.mode || '—' }}</dd>
      <dt>{{ t('novelPlay.comparison.modelFirstText') }}</dt>
      <dd data-testid="observer-call-first-text">{{ duration(call.first_token_ms) }}</dd>
      <dt>{{ t('novelPlay.comparison.providerFirstFrame') }}</dt>
      <dd>{{ duration(call.ttft_ms) }}</dd>
      <dt>{{ tr('calls.duration') }}</dt>
      <dd>{{ duration(call.duration_ms) }}</dd>
      <dt>{{ tr('calls.finishReason') }}</dt>
      <dd data-testid="observer-call-finish-reason">{{ call.finish_reason ?? '—' }}</dd>
      <dt>{{ tr('calls.protocol') }}</dt>
      <dd data-testid="observer-call-protocol">{{ call.protocol ?? '—' }}</dd>
    </dl>
    <div class="au-callbody__cols">
      <section class="au-group">
        <h4>{{ tr('calls.requestBody') }} · {{ tr('calls.messageCount', { count: messages.length }) }}</h4>
        <details v-if="call.request_body" class="au-message" data-testid="observer-call-serialized-request">
          <summary>{{ tr('calls.requestBody') }}</summary>
          <pre class="au-pre">{{ JSON.stringify(call.request_body, null, 2) }}</pre>
        </details>
        <div v-if="messages.length" class="au-scroll">
          <template v-for="message in messages" :key="message.key">
            <details v-if="message.long" class="au-message">
              <summary><span class="au-role">{{ message.role }}</span> · {{ tr('calls.chars', { count: message.text.length }) }} · {{ message.preview }}</summary>
              <pre class="au-pre">{{ message.text }}</pre>
            </details>
            <div v-else class="au-message">
              <span class="au-role">{{ message.role }}</span>
              <pre class="au-pre">{{ message.text || '—' }}</pre>
            </div>
          </template>
        </div>
        <p v-else class="au-empty" :title="tr('calls.unavailable')">{{ tr('empty') }}</p>
      </section>
      <section class="au-group">
        <h4>{{ tr('calls.output') }}</h4>
        <div v-if="call.response_body" class="au-scroll">
          <pre class="au-pre">{{ call.response_body.response_text || '—' }}</pre>
          <template v-if="call.response_body.reasoning_text">
            <h5>{{ tr('calls.reasoning') }}</h5>
            <pre class="au-pre">{{ call.response_body.reasoning_text }}</pre>
          </template>
        </div>
        <p v-else class="au-empty" :title="tr('calls.unavailable')">{{ tr('empty') }}</p>
      </section>
    </div>
  </div>
</template>
